import { CalendarDays, MapPin, Users } from 'lucide-react'
import type { ScheduledEvent } from '@/types/entities'
import { SectionCard, EmptyState, Badge } from '@/components/ui'
import { date, daysUntil } from '@/lib/format'
import type { AccountDerived } from '../lib'

const TYPE_TONE: Record<string, 'navy' | 'info' | 'warn' | 'alert' | 'neutral' | 'ok'> = {
  'Performance Review': 'navy',
  'Reporting Window': 'warn',
  'Site Visit': 'info',
  'Hearing': 'alert',
  'Deadline': 'alert',
  'Sales Meeting': 'ok',
  'Internal': 'neutral',
}

function fmtTime(iso: string) {
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export default function ScheduledTab({ d }: { d: AccountDerived }) {
  const events: ScheduledEvent[] = [...d.events].sort((a, b) => (a.start < b.start ? -1 : 1))

  if (!events.length) {
    return (
      <SectionCard pad>
        <EmptyState icon={<CalendarDays size={36} />} title="Nothing scheduled" hint="Reviews, reporting windows, site visits, and internal syncs for this account will appear here as an agenda." />
      </SectionCard>
    )
  }

  return (
    <SectionCard eyebrow="Agenda" title={<>Scheduled <b>Events</b></>} right={<span className="aw-count-pill">{events.length}</span>}>
      <ul className="aw-agenda">
        {events.map((e) => {
          const du = daysUntil(e.start.slice(0, 10))
          return (
            <li key={e.id} className="aw-agenda-item">
              <div className="aw-agenda-date">
                <span className="aw-agenda-day num">{new Date(e.start).getDate()}</span>
                <span className="aw-agenda-mon">{new Date(e.start).toLocaleDateString('en-US', { month: 'short' })}</span>
              </div>
              <div className="aw-agenda-body">
                <div className="aw-agenda-head">
                  <span className="aw-agenda-title">{e.title}</span>
                  <Badge tone={TYPE_TONE[e.type] ?? 'neutral'} variant="soft">{e.type}</Badge>
                </div>
                <div className="aw-agenda-meta">
                  <span>{date(e.start, 'med')} · {fmtTime(e.start)}{e.end ? `–${fmtTime(e.end)}` : ''}</span>
                  {du != null && <span className="aw-agenda-when">{du === 0 ? 'Today' : du > 0 ? `in ${du}d` : `${-du}d ago`}</span>}
                </div>
                <div className="aw-agenda-extra">
                  {e.location && <span className="aw-agenda-tag"><MapPin size={12} />{e.location}</span>}
                  {e.attendees && e.attendees.length > 0 && <span className="aw-agenda-tag"><Users size={12} />{e.attendees.length} attendees</span>}
                  {e.relatedPackage && <span className="aw-agenda-tag aw-agenda-tag--pkg">{e.relatedPackage}</span>}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </SectionCard>
  )
}
